import { useEffect, useRef, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import type { LucideIcon } from "lucide-react";
import {
  ArrowLeftRight,
  ArrowUpRight,
  ChevronDown,
  LayoutDashboard,
  Menu,
  RefreshCw,
  Settings as SettingsIcon,
  Shield,
  Wallet,
} from "lucide-react";

import { cn } from "@/lib/cn";
import { useMediaQuery } from "@/lib/useMediaQuery";
import { useAuth } from "@/lib/auth";
import { WalletDirectoryProvider } from "@/lib/walletDirectory";
import { AccountMenu } from "@/components/AccountMenu";
import { AccountSwitcher } from "@/components/AccountSwitcher";
import { BrandMark } from "@/components/BrandMark";
import { MobileTabBar } from "@/components/MobileTabBar";
import { ViewAsBanner } from "@/components/ViewAsBanner";
import { Badge } from "@/components/ui/Badge";
import { Drawer } from "@/components/ui/Drawer";

/**
 * M4 AppShell.
 *
 * Desktop (≥768px): vertical sidebar + content. Sidebar holds the brand mark,
 * account switcher, primary nav, admin sub-nav (role-gated, split into
 * Platform vs System sections), spacer, and a footer with the user
 * mini-card, theme toggle, and a brand/version stamp.
 *
 * Mobile (<768px): sticky header with hamburger / brand glyph / account
 * switcher (compact). The hamburger opens a full-screen sheet drawer with
 * the same nav list.
 *
 * All tap targets use `h-11 w-11` or `min-h-[44px]` per spec §9.0. The
 * <main> is given `tabIndex={-1}` and focused on route change so keyboard
 * users land at the top of each page instead of keeping focus on the link
 * they just activated (standard SPA a11y move — §14 a11y AC).
 */

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
}

const PRIMARY_NAV: NavItem[] = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard, end: true },
  // Wallets/Transactions/Sync Jobs intentionally omit `end:true` so detail
  // routes (e.g. /wallets/:id) keep their parent nav item highlighted.
  { to: "/wallets", label: "Wallets", icon: Wallet },
  { to: "/transactions", label: "Transactions", icon: ArrowLeftRight },
  { to: "/sync-jobs", label: "Sync Jobs", icon: RefreshCw },
  // Settings is also reachable via the user mini-card dropdown, but a
  // primary nav row makes it discoverable without hunting through the
  // avatar menu — important now that the Zoho Books connection list lives
  // under the account settings surface.
  { to: "/settings", label: "Settings", icon: SettingsIcon },
];

// Destinations the mobile bottom tab bar already surfaces one-tap. They're
// omitted from the hamburger drawer so it carries only the long tail (Sync
// Jobs, Account, Admin) instead of duplicating the always-visible tabs. Keep
// in sync with MobileTabBar's TABS.
const TAB_BAR_PATHS = new Set([
  "/dashboard",
  "/wallets",
  "/transactions",
  "/settings",
]);

// Admin subgroup is split into Platform (super-admin cross-tenant surfaces)
// and System (per-instance config). Both groups still gate behind the
// requireSuperAdmin role; the split is purely a chrome affordance so the
// expanded list is easier to scan.
interface AdminNavItem {
  to: string;
  label: string;
  deferred: boolean;
  /**
   * When true, the item renders as a button that opens `to` in a standalone
   * browser window (via `window.open`) instead of navigating the current tab.
   * Used for the Log Viewer so admins can keep the live tail open next to
   * their primary workspace.
   */
  openInNewWindow?: boolean;
}

const PLATFORM_NAV: AdminNavItem[] = [
  { to: "/_superadmin/accounts", label: "Accounts", deferred: false },
  { to: "/_superadmin/users", label: "Users", deferred: false },
  { to: "/_superadmin/wallets", label: "All Wallets", deferred: false },
  { to: "/_superadmin/transactions", label: "All Transactions", deferred: false },
];

const SYSTEM_NAV: AdminNavItem[] = [
  { to: "/admin/chains-tokens", label: "Chains & Tokens", deferred: false },
  { to: "/admin/tokens", label: "Verified Tokens", deferred: false },
  { to: "/_superadmin/api-keys", label: "API Keys", deferred: false },
  // Zoho OAuth backs the flagship integration; without a nav row it was only
  // reachable by hand-typing the URL.
  { to: "/_superadmin/zoho-oauth", label: "Zoho OAuth", deferred: false },
  { to: "/admin/audit-log", label: "Audit Log", deferred: true },
  { to: "/admin/logs", label: "Log Viewer", deferred: false, openInNewWindow: true },
];

// Tenant-admin surfaces. Point at the canonical `/settings/*` hub paths (the
// old /account/* URLs are now redirect stubs). Shaped as AdminNavItem so the
// existing AdminSection/AdminLink chrome renders them at the same indented
// 44px row style as the admin links.
const ACCOUNT_NAV: AdminNavItem[] = [
  { to: "/settings/members", label: "Members", deferred: false },
  { to: "/settings/account", label: "Account settings", deferred: false },
  { to: "/settings/spam-overrides", label: "Spam overrides", deferred: false },
];

// Persisted collapse state for the Admin subgroup. Default collapsed so the
// nine admin rows don't crowd the five primary items 95% of sessions use.
const NAV_ADMIN_OPEN_KEY = "nav.admin.open";

function readNavAdminOpen(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(NAV_ADMIN_OPEN_KEY) === "true";
  } catch {
    // Private-browsing / quota errors — treat as collapsed.
    return false;
  }
}

function writeNavAdminOpen(open: boolean): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(NAV_ADMIN_OPEN_KEY, open ? "true" : "false");
  } catch {
    // Best-effort — the in-memory toggle still works this session.
  }
}

export function AppShell() {
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const isDesktop = useMediaQuery("(min-width: 768px)");
  const location = useLocation();
  const mainRef = useRef<HTMLElement>(null);

  // Focus <main> on each route change so keyboard/screen-reader users land
  // at the top of the new page rather than keeping focus on the nav item
  // they just activated.
  useEffect(() => {
    mainRef.current?.focus();
  }, [location.pathname]);

  // Close the mobile drawer when crossing the breakpoint — otherwise it
  // sticks around invisibly and traps focus on the next open.
  useEffect(() => {
    if (isDesktop && mobileNavOpen) setMobileNavOpen(false);
  }, [isDesktop, mobileNavOpen]);

  return (
    <WalletDirectoryProvider>
    <div className="flex min-h-screen bg-bg text-text">
      {isDesktop && <Sidebar />}

      <div className="flex min-w-0 flex-1 flex-col">
        {/* Sticky amber impersonation banner. Rendered as the FIRST child of
            the main column so it owns `top-0`; it publishes its measured
            height to `--viewas-h`, which the mobile header reads for its own
            sticky offset so the two stack instead of overlapping. Renders
            nothing (and clears the var) when not in view-as mode. */}
        <ViewAsBanner />
        {!isDesktop && (
          <header
            // `top` comes from --viewas-h (0 when no banner) so the header
            // pins directly beneath the impersonation strip rather than under
            // it at the same offset.
            style={{ top: "var(--viewas-h, 0px)" }}
            className={cn(
              "sticky z-30 flex h-14 items-center justify-between gap-2",
              // v2 mobile header: hairline only, no shadow.
              "border-b border-[var(--line)] bg-[var(--surface)] px-4",
            )}
          >
            <button
              type="button"
              onClick={() => setMobileNavOpen(true)}
              aria-label="Open navigation menu"
              className={cn(
                "inline-flex h-11 w-11 items-center justify-center rounded-[var(--r-md)]",
                "text-[var(--ink-2)] transition-colors duration-fast ease hover:bg-[var(--surface-2)] hover:text-[var(--ink)]",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--indigo)]",
              )}
            >
              <Menu className="h-5 w-5" strokeWidth={1.5} aria-hidden />
            </button>

            <BrandMark variant="glyph" />

            <AccountSwitcher compact />
          </header>
        )}

        <main
          ref={mainRef}
          tabIndex={-1}
          // On mobile the fixed 60px tab bar overlays the bottom of the
          // viewport; pad the scroll content (plus the iOS home-indicator
          // safe-area) so nothing hides behind it.
          style={
            !isDesktop
              ? { paddingBottom: "calc(60px + env(safe-area-inset-bottom))" }
              : undefined
          }
          className="min-w-0 flex-1 bg-bg focus:outline-none"
        >
          <Outlet />
        </main>
      </div>

      {/* Persistent one-tap bottom nav for the primary destinations. The
          hamburger drawer keeps the long tail (Sync Jobs, Admin, account
          switcher, sign out). */}
      {!isDesktop && <MobileTabBar />}

      {!isDesktop && (
        <Drawer
          open={mobileNavOpen}
          onOpenChange={setMobileNavOpen}
          mode="sheet"
          title="Navigation"
        >
          <div className="flex h-full flex-col">
            <div className="px-4 pt-4">
              <AccountSwitcher />
            </div>
            {/* Long tail only — the four tab-bar destinations are omitted so
                the drawer doesn't duplicate the persistent bottom tabs. */}
            <nav className="flex flex-col gap-1 p-4" aria-label="Primary">
              {PRIMARY_NAV.filter((item) => !TAB_BAR_PATHS.has(item.to)).map(
                (item) => (
                  <SidebarLink
                    key={item.to}
                    item={item}
                    onNavigate={() => setMobileNavOpen(false)}
                  />
                ),
              )}
              <AccountGroup />
              <AdminGroup />
            </nav>
            <div className="mt-auto flex flex-col gap-2 border-t border-border p-3">
              <UserMiniCard onNavigate={() => setMobileNavOpen(false)} />
            </div>
          </div>
        </Drawer>
      )}
    </div>
    </WalletDirectoryProvider>
  );
}

/* ---------------------- Sidebar --------------------- */

function Sidebar() {
  return (
    <aside
      className={cn(
        "sticky top-0 hidden h-screen w-56 shrink-0 flex-col",
        "border-r border-border bg-surface md:flex",
      )}
    >
      {/* Brand mark pinned top. */}
      <div className="p-4">
        <BrandMark to="/dashboard" />
      </div>
      {/* Scrollable middle: account chip + all nav. With the Admin group
          expanded a super admin's list can exceed the viewport height; this
          overflow container keeps the pinned footer (and its only sign-out
          affordance) reachable instead of clipping it off an h-screen aside. */}
      <div className="flex-1 overflow-y-auto overscroll-contain">
        <div className="px-3 pb-2">
          <AccountSwitcher />
        </div>
        <nav className="flex flex-col gap-0.5 px-3" aria-label="Primary">
          {PRIMARY_NAV.map((item) => (
            <SidebarLink key={item.to} item={item} />
          ))}
          <AccountGroup />
          <AdminGroup />
        </nav>
      </div>
      {/* User mini-card + wordmark stamp pinned bottom. */}
      <div className="flex flex-col gap-2 border-t border-border p-3">
        <UserMiniCard />
        <span className="px-2 text-[11px] text-[var(--ink-4)]">Cawamba</span>
      </div>
    </aside>
  );
}

/* ---------------------- Nav Link --------------------- */

interface SidebarLinkProps {
  item: NavItem;
  onNavigate?: () => void;
}

function SidebarLink({ item, onNavigate }: SidebarLinkProps) {
  const Icon = item.icon;
  return (
    <NavLink
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      className={({ isActive }) =>
        cn(
          "flex min-h-[44px] items-center gap-3 rounded-[var(--r-md)] px-3 py-2 text-[13px] font-medium",
          "transition-colors duration-fast ease",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--indigo)]",
          // v2 active row: solid ink fill, paper text. No left stripe.
          isActive
            ? "bg-[var(--ink)] text-[var(--bg)]"
            : "text-[var(--ink-2)] hover:bg-[var(--surface-2)] hover:text-[var(--ink)]",
        )
      }
    >
      <Icon className="h-4 w-4 shrink-0" strokeWidth={1.5} aria-hidden />
      {item.label}
    </NavLink>
  );
}

/* ---------------------- Admin subgroup --------------------- */

function AdminGroup() {
  const { isSuperAdmin } = useAuth();
  // Default collapsed, persisted across sessions (see readNavAdminOpen). Lazy
  // initializer so the localStorage read happens once on mount.
  const [open, setOpen] = useState(readNavAdminOpen);
  // /admin/* routes are super-admin only (backend uses requireSuperAdmin).
  // Hiding the group for tenant admins prevents a menu-into-403 dead-end.
  if (!isSuperAdmin) return null;

  const toggle = () =>
    setOpen((v) => {
      const next = !v;
      writeNavAdminOpen(next);
      return next;
    });

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={toggle}
        aria-expanded={open}
        className={cn(
          "flex w-full min-h-[44px] items-center gap-3 rounded-[var(--r-md)] px-3 py-2 text-[13px] font-medium",
          "text-[var(--ink-2)] transition-colors duration-fast ease hover:bg-[var(--surface-2)] hover:text-[var(--ink)]",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--indigo)]",
        )}
      >
        <Shield className="h-4 w-4 shrink-0" strokeWidth={1.5} aria-hidden />
        <span className="flex-1 text-left">Admin</span>
        <ChevronDown
          className={cn(
            "h-4 w-4 transition-transform duration-fast ease",
            open ? "rotate-0" : "-rotate-90",
          )}
          strokeWidth={1.5}
          aria-hidden
        />
      </button>
      {open && (
        <div className="flex flex-col gap-2 pt-1">
          <AdminSection heading="Platform" items={PLATFORM_NAV} />
          <AdminSection heading="System" items={SYSTEM_NAV} />
        </div>
      )}
    </div>
  );
}

/* ---------------------- Account subgroup --------------------- */

/**
 * Tenant-admin nav group — the sidebar's only discoverable surface for the
 * Members / Account settings / Spam overrides pages (otherwise buried in the
 * avatar dropdown). Always expanded; gated on the active-account ADMIN role
 * (which also resolves true for super admins). Reuses AdminSection so the
 * heading + indented 44px rows match the Platform/System treatment exactly.
 */
function AccountGroup() {
  const { hasAccountRole } = useAuth();
  if (!hasAccountRole("ADMIN")) return null;

  return (
    <div className="mt-2">
      <AdminSection heading="Account" items={ACCOUNT_NAV} />
    </div>
  );
}

function AdminSection({
  heading,
  items,
}: {
  heading: string;
  items: AdminNavItem[];
}) {
  return (
    <div className="flex flex-col gap-0.5">
      {/* Real heading element so screen-reader users can skim the admin tree
          via landmark/heading rotor. Visual style is unchanged — h3 under the
          sidebar nav heading stack. */}
      <h3 className="px-3 pb-1 pt-2 text-label">
        {heading}
      </h3>
      <ul
        className="flex flex-col gap-0.5 pl-3"
        role="group"
        aria-label={heading}
      >
        {items.map((item) => (
          <li key={item.label}>
            <AdminLink item={item} />
          </li>
        ))}
      </ul>
    </div>
  );
}

function AdminLink({ item }: { item: AdminNavItem }) {
  const baseRow = cn(
    "flex w-full min-h-[44px] items-center justify-between gap-2 rounded-[var(--r-md)] px-3 py-2 text-[13px]",
    "transition-colors duration-fast ease",
    "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--indigo)]",
  );

  if (item.openInNewWindow) {
    return (
      <button
        type="button"
        // Screen readers don't infer from the ArrowUpRight icon that the row
        // jumps to a new window. Spell it out so users know their tab won't
        // navigate away. Also add `noreferrer` alongside `noopener` — belt
        // and suspenders for reverse-tabnabbing / referrer leaks.
        aria-label={`${item.label} (opens in new window)`}
        onClick={() =>
          window.open(
            item.to,
            "_blank",
            "width=1400,height=900,noopener,noreferrer",
          )
        }
        className={cn(
          baseRow,
          "text-[var(--ink-2)] hover:bg-[var(--surface-2)] hover:text-[var(--ink)]",
        )}
      >
        <span className="inline-flex items-center gap-2">
          <span>{item.label}</span>
          <ArrowUpRight
            className="h-3.5 w-3.5 text-[var(--ink-3)]"
            strokeWidth={1.5}
            aria-hidden
          />
        </span>
        {item.deferred && <Badge variant="info">v1.1</Badge>}
      </button>
    );
  }

  return (
    <NavLink
      to={item.to}
      className={({ isActive }) =>
        cn(
          baseRow,
          // v2 selected affordance: solid ink fill + paper text.
          isActive
            ? "bg-[var(--ink)] text-[var(--bg)]"
            : "text-[var(--ink-2)] hover:bg-[var(--surface-2)] hover:text-[var(--ink)]",
        )
      }
    >
      <span>{item.label}</span>
      {item.deferred && <Badge variant="info">v1.1</Badge>}
    </NavLink>
  );
}

/* ---------------------- User mini-card --------------------- */

function UserMiniCard({ onNavigate }: { onNavigate?: () => void } = {}) {
  const { user, roleInActiveAccount, isSuperAdmin } = useAuth();

  if (!user) return null;

  // Unicode-safe leading-grapheme extraction. `.slice(0, 1)` would split a
  // surrogate pair (emoji, non-BMP) into a lone half and render garbled.
  // Spread through the string iterator to grab the whole code point.
  const initial = [...user.email.trim()][0]?.toUpperCase() ?? "—";
  const roleLabel = isSuperAdmin ? "SUPER ADMIN" : roleInActiveAccount() ?? "";

  // Delegate the menu itself to the shared <AccountMenu> so the sidebar footer
  // and (any future) avatar trigger surface one identical menu — Settings,
  // Change password, role-gated account items, Sign out. This card only owns
  // the trigger row (email + role); the dropdown content lives in one place.
  return (
    <AccountMenu
      side="top"
      align="start"
      onNavigate={onNavigate}
      trigger={
        <button
          type="button"
          aria-label="Account menu"
          className={cn(
            "flex w-full min-h-[44px] items-center gap-3 rounded-[var(--r-md)] p-2 text-left",
            "text-[var(--ink-2)] transition-colors duration-fast ease hover:bg-[var(--surface-2)] hover:text-[var(--ink)]",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--indigo)]",
          )}
        >
          <span
            aria-hidden
            className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[var(--surface-2)] font-mono text-sm text-[var(--ink)]"
          >
            {initial}
          </span>
          <span className="flex min-w-0 flex-1 flex-col">
            <span className="truncate text-[13px] text-[var(--ink)]">
              {user.email}
            </span>
            <span className="text-label lowercase first-letter:uppercase">
              {roleLabel}
            </span>
          </span>
          {/* Visual chevron makes it obvious the row is a menu trigger
              (sign out / settings live in the dropdown, not as separate
              rows in the sidebar). */}
          <ChevronDown
            className="h-4 w-4 shrink-0 text-[var(--ink-3)]"
            strokeWidth={1.5}
            aria-hidden
          />
        </button>
      }
    />
  );
}
